import { Field, PasswordInput } from '@/components';
import { trans } from '@/i18n';
import { convertFullWidthToHalfWidth, formatCurrency, formatPostalCode, formatPhoneNumber, unformatCurrency, formatSubDomain } from '@/utils/helpers';
import { GridItem, Input } from '@chakra-ui/react';
import React, { ChangeEventHandler } from 'react';
import { Control, Controller } from 'react-hook-form';

type FormatType = 'postal' | 'phone' | 'subDomain';
interface IProps {
  span?: number | 'auto';
  name: string;
  label: string;
  disabled?: boolean;
  hidePlaceholder?: boolean;
  placeholder?: string;
  isPassword?: boolean;
  control?: Control<any>;
  errors: any;
  width?: string | number;
  value?: string;
  onChange?: ChangeEventHandler<HTMLInputElement> | undefined;
  childName?: string;
  childIndex?: number;
  onFocus?: () => void;
  onClick?: () => void;
  className?: string;
  horizontal?: boolean;
  isRequired?: boolean;
  isCurrency?: boolean;
  formatType?: FormatType;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
}

function BaseInput(props: IProps) {
  const InputField = props.isPassword ? PasswordInput : Input;

  const gridProps: any = {
    colSpan: props.span || 'auto',
  };
  if (props.width) gridProps.width = props.width;

  const handleCurrencyChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldOnChange: (value: any) => void
  ) => {
    if (props.isCurrency) {
      const rawValue = unformatCurrency(e.target.value);
      const formattedValue = formatCurrency(rawValue);
      e.target.value = formattedValue;
      fieldOnChange(rawValue);
    }
  };

  const handleFormatChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldOnChange: (value: any) => void
  ) => {
    let processedValue = convertFullWidthToHalfWidth(e.target.value)

    if (props.formatType === 'postal') {
      processedValue = formatPostalCode(processedValue)
    } else if (props.formatType === 'phone') {
      processedValue = formatPhoneNumber(processedValue)
    } else if (props.formatType === 'subDomain') {
      processedValue = formatSubDomain(processedValue)
    }

    if (processedValue !== props.value) {
      const newEvent = {
        ...e,
        target: {
          ...e.target,
          value: processedValue,
        },
      }

      fieldOnChange?.(newEvent as React.ChangeEvent<HTMLInputElement>)
      return
    }
  };

  const handlePaste = React.useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      if (props.formatType) {
        e.preventDefault()

        const pastedText = e.clipboardData.getData('text/plain')

        const cleanedText = pastedText.replace(/[^\d\-]/g, '')

        const syntheticEvent = {
          target: {
            value: cleanedText,
          },
        } as React.ChangeEvent<HTMLInputElement>

        props.onChange?.(syntheticEvent)
      }

      props.onPaste?.(e)
    },
    [props.formatType, props.onPaste, props.onChange],
  )

  return (
    <GridItem {...gridProps}>
      <Controller
        control={props.control}
        // render={({ field: { onChange, onBlur, value } }) => (
        render={({ field }) => {
          const displayValue =
            props.isCurrency && field.value
              ? formatCurrency(field.value)
              : props.onChange !== undefined
                ? props.value
                : field.value;

          return (
            <Field
              label={props.label}
              isRequired={props.isRequired}
              invalid={
                props.childName && props.childIndex !== undefined
                  ? !!props.errors[props.childName]?.[props.childIndex]
                  : !!props.errors[props.name]
              }
              errorText={
                props.childName && props.childIndex !== undefined
                  ? props.errors[props.childName]?.[props.childIndex]?.message
                  : props.errors[props.name]?.message
              }
              className={`${props.horizontal ? 'input-h' : ''} ${props.className}`}
            >
              <InputField
                {...field}
                value={displayValue}
                disabled={props.disabled}
                placeholder={
                  props.hidePlaceholder
                    ? ''
                    : props.placeholder || trans('shared.input', { key: props.label })
                }
                _placeholder={{
                  color: '#A0AEC0',
                }}
                onChange={(e) => {
                  if (props.formatType) {
                    handleFormatChange(e, field.onChange);
                  } else if (props.isCurrency) {
                    handleCurrencyChange(e, field.onChange);
                  } else if (props.name === 'phone') {
                    handleFormatChange(e, field.onChange);
                  }
                  else {
                    props.onChange?.(e);
                    field.onChange(e);
                  }
                }}
                onFocus={props.onFocus}
                onClick={props.onClick}
                onPaste={handlePaste}
              />
            </Field>
          );
        }}
        name={props.name}
      />
    </GridItem>
  );
}

export { BaseInput };
